import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import Can from '../../../../features/components/Can'
import CampaignsChangeHistory from '../../CampaignsChangeHistory'
import ListItemsTable from '../../../../features/components/ListItemsTable'
import ListItemsTotals from '../../../../features/components/ListItemsTotals'
import SocialAuthChecker from '../../../../features/components/SocialAuthChecker'
import CampaignFacebookEdit from '../../../../forms/Facebook/CampaignForms/CampaignFacebookEdit'
import CampaignFacebookDuplicate from '../../../../forms/Facebook/CampaignForms/CampaignFacebookDuplicate'

import useCampaignFetchData from '../../hooks'
import useGetFacebookCampaignActions from './useGetFacebookCampaignActions'

import { createCampaignSummaryPageLink } from '../../helpers'

import {
  campaignsSelector,
  campaignsTotalsSelector,
  campaignsIsLoadingSelector,
  campaignsWasLoadedSelector,
  campaignsErrorSelector,
  campaignDeleteSelector,
  campaignUpdateIsLoadingSelector,
  campaignUpdateSelector,
  campaignsAfterSelector,
  campaignSessionIdSelector,
  campaignsNextSelector
} from '../../../../modules/selectors/campaigns'
import { selectedFacebookAdAccountSelector } from '../../../../modules/selectors/adAccounts'

import { ROUTE_PARAMS } from '../../../../constants/routes'
import { FACEBOOK_PLATFORM } from '../../../../constants/selectLists/platformList'

const FacebookCampaignsSection = ({ onRowCheck, handleDeleteRequest }) => {
  const campaigns = useSelector(campaignsSelector)
  const campaignsIsLoading = useSelector(campaignsIsLoadingSelector)
  const campaignsAfter = useSelector(campaignsAfterSelector)
  const campaignsNext = useSelector(campaignsNextSelector)
  const campaignsSessionId = useSelector(campaignSessionIdSelector)
  const campaignUpdate = useSelector(campaignUpdateSelector)
  const campaignUpdateIsLoading = useSelector(campaignUpdateIsLoadingSelector)

  const { id: campaignUpdateId } = campaignUpdate

  const campaignDelete = useSelector(campaignDeleteSelector)
  const { id: campaignDeleteId, isLoading: campaignDeleteIsLoading } = campaignDelete

  const totalsItem = useSelector(campaignsTotalsSelector)

  const { id: facebookAdAccountId, currency_symbol: currencySymbol } = useSelector(selectedFacebookAdAccountSelector)

  const showTotalsItem = !campaignsIsLoading && totalsItem && campaigns.length > 1

  const getCampaignsData = useCampaignFetchData({
    adAccountId: facebookAdAccountId,
    platform: FACEBOOK_PLATFORM,
    withPagination: true
  })

  const loadMoreCampaignsHandler = useCallback(() => {
    getCampaignsData(campaignsAfter, campaignsSessionId, { skipClearingCampaigns: true })
  }, [getCampaignsData, campaignsAfter, campaignsSessionId])

  const getFacebookCampaignActions = useGetFacebookCampaignActions(handleDeleteRequest)

  const summaryPageLinkExtraParams = useMemo(
    () => ({ [ROUTE_PARAMS.adAccount]: facebookAdAccountId }),
    [facebookAdAccountId]
  )

  return (
    <SocialAuthChecker
      platform={FACEBOOK_PLATFORM}
      dataFetch={getCampaignsData}
      adAccountId={facebookAdAccountId}
      authFailureText="Please authenticate with Facebook to retrieve the list of campaigns."
    >
      <ListItemsTable
        items={campaigns}
        platform={FACEBOOK_PLATFORM}
        currencySymbol={currencySymbol}
        isLoadingSelector={campaignsIsLoadingSelector}
        errorSelector={campaignsErrorSelector}
        wasLoadedSelector={campaignsWasLoadedSelector}
        createItemLinkHelper={createCampaignSummaryPageLink}
        getActionsDropdownOptions={getFacebookCampaignActions}
        createItemLinkExtraParams={summaryPageLinkExtraParams}
        itemUpdatingId={campaignUpdateIsLoading ? campaignUpdateId : ''}
        itemDeletingId={campaignDeleteIsLoading ? campaignDeleteId : ''}
        nameColumnLabel="Campaign"
        loadMoreDataHandler={campaignsNext ? loadMoreCampaignsHandler : null}
        showRowCheckbox={true}
        onRowCheck={onRowCheck}
        showGoal={true}
      >
        {showTotalsItem && (
          <ListItemsTotals
            items={campaigns}
            data={totalsItem}
            platform={FACEBOOK_PLATFORM}
            currencySymbol={currencySymbol}
            showRowCheckbox={true}
            showGoal={true}
          />
        )}
      </ListItemsTable>
      <Can I="update" a="Campaign">
        <CampaignFacebookEdit shouldBeCleared />
      </Can>
      <Can I="create" a="Campaign">
        <CampaignFacebookDuplicate />
      </Can>
      <CampaignsChangeHistory platform={FACEBOOK_PLATFORM} />
    </SocialAuthChecker>
  )
}

export default FacebookCampaignsSection
